// ─────────────────────────────────────────────────────────────────────────────
// Blank Theme — Random-Layout Generator (in-editor)
//
// Regenerates the CURRENTLY OPEN photobook / layflat design so that every
// interior (non-cover) page is filled with a random, non-repeating layout whose
// image count falls in a user-chosen range.
//
// Design principle (see docs/blank-theme-generator-2026-07-08.md): do NOT
// hand-roll any layout→canvas scaling. Reuse the SAME reducers the Layout
// sidebar's Shuffle already uses (`setPageLayout` / `setEntireSpreadLayout`),
// which own the scaling + bleed math, and just drive them programmatically.
//
// Two modes (chosen in BlankThemeDialog):
//   • "empty" — clear interior pages first, then fill with EMPTY image boxes
//     (a fresh skeleton the user drops photos into afterwards).
//   • "keep"  — do NOT clear; the apply reducers reflow the page's existing
//     photos into the new random layout.
//
// Cover / inside-cover pages are NEVER modified in either mode.
// ─────────────────────────────────────────────────────────────────────────────

import { v4 as uuidv4 } from "uuid";
import { store, setSuppressLayoutHistory } from "../../../store/store.jsx";
import {
  applyTheme,
  setPageNumber,
  setActiveSide,
  setPageLayout,
  setEntireSpreadLayout,
  setCurrentObjectProperties,
} from "../../../store/slices/canvas.js";
import { EDITOR_TYPES } from "../constants/index.js";
import { scaleLayout } from "../common-functions/index.js";
import { fetchAllLayoutsCached, fetchAutoLayoutsCached } from "../services/layoutsCache.js";

// ── Structure helpers ────────────────────────────────────────────────────────

// A page uses a 2-up spread layout (vs a single full-width layout) when the
// product is a photobook, or a foldable full-sheet. This MUST mirror the Layout
// sidebar's `scaleLayouts` split (photobook / foldable → splitby 2).
const pageUsesSpread = (editorType, settings, page) =>
  editorType === EDITOR_TYPES.PHOTOBOOK ||
  (settings?.isFoldable === true && page?.settings?.isHalfSheet !== true);

const usesHalfWidthLayout = (editorType, settings) =>
  editorType === EDITOR_TYPES.PHOTOBOOK || settings?.isFoldable === true;

// Split the current pages into leading structural (front cover / inside-front),
// interior (the pages we regenerate), and trailing structural (inside-back /
// back cover). Photobook covers are POSITIONAL (indices 0,1,len-2,len-1);
// layflat covers carry an `isCoverPage` flag.
const splitStructural = (pages, editorType) => {
  const len = pages.length;

  if (editorType === EDITOR_TYPES.PHOTOBOOK) {
    // Canonical structure is always [front, insideFront, ...interior,
    // insideBack, back] (see addNewBlankPage in canvas.js) — even with full
    // cover / hideLastCover, only RENDERING changes, the data keeps all four.
    // len === 4 is covers-only (empty interior) — still split so an admin can
    // add interior spreads BETWEEN insideFront and insideBack, not after back.
    if (len < 4) {
      return { leading: pages.slice(), interior: [], trailing: [] };
    }
    return {
      leading: pages.slice(0, 2),
      interior: pages.slice(2, len - 2),
      trailing: pages.slice(len - 2),
    };
  }

  // Layflat / foldable — covers are flagged. Peel matching cover pages off the
  // front and back; everything between is interior.
  let start = 0;
  while (start < len && pages[start]?.isCoverPage === true) start += 1;
  let end = len;
  while (end > start && pages[end - 1]?.isCoverPage === true) end -= 1;

  return {
    leading: pages.slice(0, start),
    interior: pages.slice(start, end),
    trailing: pages.slice(end),
  };
};

const clonePage = (page) => JSON.parse(JSON.stringify(page));

// Blank a page's content but keep its structural identity (id, title, settings,
// isCoverPage, pageNumber). `layout: []` lets the apply reducers rebuild the
// sides fresh with empty boxes.
const blankInteriorFromExisting = (page) => ({
  ...clonePage(page),
  layout: [],
  bgColor: "#fff",
  isPageEdited: false,
});

const makeBlankInterior = (index) => ({
  id: `pages_${uuidv4()}`,
  title: `page${index + 1}`,
  bgColor: "#fff",
  layout: [],
  settings: { isHalfSheet: false },
  isPageEdited: false,
});

// ── Layout pool (cache-first, scaled to target size) ─────────────────────────

// Clone the raw cached layouts (fetchAllLayoutsCached hands back UNSCALED items
// shared by every consumer — never mutate them) and scale each to the current
// canvas size. Mirrors LayoutsAction.scaleLayouts exactly.
const scalePool = (rawItems, canvasSize, splitby) =>
  (rawItems || []).map((it) => {
    const cloned = {
      ...it,
      layout_c: JSON.parse(JSON.stringify(it.layout_c)),
    };
    cloned.layout_c.layout = cloned.layout_c.layout.map((layout) =>
      scaleLayout(layout, canvasSize.width / splitby, canvasSize.height)
    );
    return cloned;
  });

// Pick a layout with `target` image boxes, preferring one not already used.
// Falls back to the NEAREST available image count (like the Shuffle fallback)
// when no layout has exactly `target`. Only repeats a layout once a count is
// fully exhausted. Returns the chosen item (or null if the pool is empty).
const pickLayout = (pool, target, usedIds) => {
  if (!pool || pool.length === 0) return null;

  let candidates = pool.filter((l) => l.number_of_images === target);

  if (candidates.length === 0) {
    // Search outward for the nearest count that has any layouts.
    for (let step = 1; step <= 30; step += 1) {
      const up = pool.filter((l) => l.number_of_images === target + step);
      if (up.length) {
        candidates = up;
        break;
      }
      const down = pool.filter((l) => l.number_of_images === target - step);
      if (down.length) {
        candidates = down;
        break;
      }
    }
  }

  if (candidates.length === 0) return null;

  const unused = candidates.filter((l) => !usedIds.has(l._id));
  const chooseFrom = unused.length ? unused : candidates;
  const chosen = chooseFrom[Math.floor(Math.random() * chooseFrom.length)];
  if (chosen?._id != null) usedIds.add(chosen._id);
  return chosen;
};

const randomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// Total image boxes on a page across all its layout sides.
const countPageImages = (page) =>
  (page?.layout || []).reduce(
    (n, l) => n + (l?.objects || []).filter((o) => o?.type === "img").length,
    0
  );

// Remembers the settings of the most recent generation this session so a
// "Regenerate" affordance can re-roll with the same choices even after the
// sidebar panel remounts. Not persisted across reloads (intentional).
let lastGenerationConfig = null;
export const getLastGenerationConfig = () => lastGenerationConfig;

// ─────────────────────────────────────────────────────────────────────────────
// Public API
//
// generateRandomLayoutPages(dispatch, {
//   mode: "empty" | "keep",
//   minImages, maxImages,
//   targetInteriorCount?: number,   // admin-only page-count override; when
//                                   // omitted the current interior count is kept
// })
//
// Returns the deep-cloned built pages (so a caller can serialise if needed).
// Throws if there is no usable layout pool.
// ─────────────────────────────────────────────────────────────────────────────
export const generateRandomLayoutPages = async (
  dispatch,
  {
    mode = "empty",
    minImages = 1,
    maxImages = 5,
    targetInteriorCount,
    // When true (only meaningful with mode "keep"), each page is re-rolled to a
    // NEW layout that has the SAME number of image boxes it currently holds, so
    // existing photos reflow 1:1 and nothing is dropped — a faithful "shuffle
    // this book's layouts" for editing an existing theme.
    preserveImageCount = false,
    // What minImages/maxImages count: "spread" = the whole 2-page view (layout
    // templates are spread-wide, so no scaling); "page" = each single page (a
    // spread's target is doubled). Only affects photobook/foldable spreads.
    countScope = "spread",
    // Draw from the layouts ticked for Auto Layout instead of the whole
    // catalog. Set by CreateThemeDialog and Shuffle All; BlankThemeDialog
    // deliberately leaves it off and keeps using everything.
    autoLayoutOnly = false,
  } = {}
) => {
  const state = store.getState();
  const present = state.canvas.present;
  const editorType = present.editorType;
  const settings = present.settings || {};
  const canvasSize = present.canvasSize;
  const currentPages = present.pages || [];
  // Remember where the user was so we can land them back there after filling
  // (the fill loop navigates page-by-page) instead of jumping to the cover.
  const startPageIndex = present.activePageIndex || 0;

  const lo = Math.max(1, Math.min(minImages, maxImages));
  const hi = Math.max(lo, Math.max(minImages, maxImages));

  // Remember for one-click re-roll.
  lastGenerationConfig = { mode, minImages: lo, maxImages: hi };

  // The public API speaks PHYSICAL pages, but each interior entry is a spread
  // (2 physical pages) for photobook/foldable and a single page for layflat.
  // `pagesPerEntry` bridges the two so counts/photos map to real pages.
  const pagesPerEntry = usesHalfWidthLayout(editorType, settings) ? 2 : 1;

  // 1. Split current pages and build the target pages array (covers preserved).
  const { leading, interior, trailing } = splitStructural(
    currentPages,
    editorType
  );

  let interiorPages =
    mode === "empty"
      ? interior.map(blankInteriorFromExisting)
      : interior.map(clonePage);

  // Admin page-count override — grow/shrink the interior only. `targetInteriorCount`
  // arrives as physical PAGES; convert to interior entries (spreads for photobook).
  if (typeof targetInteriorCount === "number" && targetInteriorCount >= 0) {
    const targetEntries =
      targetInteriorCount <= 0
        ? 0
        : Math.max(1, Math.round(targetInteriorCount / pagesPerEntry));
    if (targetEntries !== interiorPages.length) {
      if (targetEntries < interiorPages.length) {
        interiorPages = interiorPages.slice(0, targetEntries);
      } else {
        while (interiorPages.length < targetEntries) {
          interiorPages.push(makeBlankInterior(interiorPages.length));
        }
      }
    }
  }

  const targetPages = [
    ...leading.map(clonePage),
    ...interiorPages,
    ...trailing.map(clonePage),
  ];

  const interiorStart = leading.length;
  const interiorEnd = interiorStart + interiorPages.length; // exclusive

  if (interiorPages.length === 0) {
    // Nothing to fill (e.g. a stray tiny page array). Still apply so an admin
    // count reduction to 0 commits, but there's nothing to randomise.
    dispatch(setCurrentObjectProperties(null));
    dispatch(applyTheme(targetPages));
    return JSON.parse(JSON.stringify(store.getState().canvas.present.pages));
  }

  // 2. Fetch + scale the layout pools we actually need (cache-first).
  const splitby = usesHalfWidthLayout(editorType, settings) ? 2 : 1;

  // Which pools does this run need? Decide per interior page.
  const anySpread = interiorPages.some((p) =>
    pageUsesSpread(editorType, settings, p)
  );
  const anySingle = interiorPages.some(
    (p) => !pageUsesSpread(editorType, settings, p)
  );

  // `autoLayoutOnly` callers ask the API for the ticked layouts only
  // (filter.auto_layout: true). Nobody has ticked much yet, so an empty pool
  // for a shape this run needs falls back to the full catalog rather than
  // failing — the fallback stops firing the moment a working set is ticked.
  let rawPage;
  let rawSpread;
  if (autoLayoutOnly) {
    const auto = await fetchAutoLayoutsCached();
    const missingShape =
      (anySpread && auto.spread.length === 0) ||
      (anySingle && auto.page.length === 0);
    if (missingShape) {
      const full = await fetchAllLayoutsCached();
      rawPage = full.page;
      rawSpread = full.spread;
    } else {
      rawPage = auto.page;
      rawSpread = auto.spread;
    }
  } else {
    const full = await fetchAllLayoutsCached();
    rawPage = full.page;
    rawSpread = full.spread;
  }

  const spreadPool = anySpread ? scalePool(rawSpread, canvasSize, splitby) : [];
  const pagePool = anySingle ? scalePool(rawPage, canvasSize, splitby) : [];

  if (
    (anySpread && spreadPool.length === 0) &&
    (anySingle && pagePool.length === 0)
  ) {
    throw new Error("No layouts are available to generate a design.");
  }

  // 3. Commit the target pages, then fill each interior page.
  //
  // Undo strategy: this whole operation must be ONE undo step. Suppress history
  // recording for every intermediate dispatch, then let the FINAL layout dispatch
  // record exactly one checkpoint (which captures the pre-generation state), and
  // suppress the trailing page-restore dispatches too. Result: a single Ctrl+Z
  // reverts the entire generation, and prior history is preserved (no clearHistory).
  setSuppressLayoutHistory(true);
  try {
    dispatch(setCurrentObjectProperties(null));
    dispatch(applyTheme(targetPages));

    const usedIds = new Set();

    for (let i = interiorStart; i < interiorEnd; i += 1) {
      const page = targetPages[i];
      // Double the target for a spread ONLY when counting per single page
      // ("page" scope). "spread" scope uses the count as-is (templates are
      // spread-wide). `existingCount` (keep-content) needs no scaling.
      const perPageFactor =
        countScope === "page" && pageUsesSpread(editorType, settings, page) ? 2 : 1;
      const existingCount = preserveImageCount ? countPageImages(page) : 0;
      const target =
        existingCount > 0
          ? existingCount
          : randomInt(lo * perPageFactor, hi * perPageFactor);
      const isLast = i === interiorEnd - 1;
      dispatch(setPageNumber(i));

      if (pageUsesSpread(editorType, settings, page)) {
        const chosen = pickLayout(spreadPool, target, usedIds);
        if (chosen?.layout_c?.layout) {
          if (isLast) setSuppressLayoutHistory(false); // records the ONE checkpoint
          dispatch(setEntireSpreadLayout(chosen.layout_c.layout));
          if (isLast) setSuppressLayoutHistory(true);
        }
      } else {
        dispatch(setActiveSide(0));
        const chosen = pickLayout(pagePool, target, usedIds);
        if (chosen?.layout_c?.layout?.[0]) {
          if (isLast) setSuppressLayoutHistory(false);
          dispatch(setPageLayout(chosen.layout_c.layout[0]));
          if (isLast) setSuppressLayoutHistory(true);
        }
      }
    }

    // Land the user back on the page they were on (clamped in case an admin
    // page-count change shrank the book), NOT the cover. Suppressed so these
    // add no undo step of their own.
    const restoreIndex = Math.min(
      Math.max(0, startPageIndex),
      targetPages.length - 1
    );
    dispatch(setPageNumber(restoreIndex));
    dispatch(setActiveSide(0));
    dispatch(setCurrentObjectProperties(null));
  } finally {
    setSuppressLayoutHistory(false);
  }

  return JSON.parse(JSON.stringify(store.getState().canvas.present.pages));
};

// Count the interior (regenerated) ENTRIES of the currently open design.
export const getCurrentInteriorCount = () => {
  const present = store.getState().canvas.present;
  const { interior } = splitStructural(present.pages || [], present.editorType);
  return interior.length;
};

// Same, but in PHYSICAL pages (a photobook/foldable entry = 2 pages) — used by
// the dialog so its "Number of pages" input speaks real pages, covers excluded.
export const getCurrentInteriorPageCount = () => {
  const present = store.getState().canvas.present;
  const { interior } = splitStructural(present.pages || [], present.editorType);
  const factor = usesHalfWidthLayout(present.editorType, present.settings || {})
    ? 2
    : 1;
  return interior.length * factor;
};

// Whether the blank generator applies to the current product (spread-based only).
export const isBlankGeneratorSupported = (editorType) =>
  editorType === EDITOR_TYPES.PHOTOBOOK ||
  editorType === EDITOR_TYPES.LAYFLATALBUM;

// Does the current product lay out in 2-page spreads? (drives the per-page vs
// per-spread photo-count choice in the dialog). Layflat = single pages → false.
export const productUsesSpreads = () => {
  const present = store.getState().canvas.present;
  return usesHalfWidthLayout(present.editorType, present.settings || {});
};
